import { Component, type ReactNode } from "react";

/**
 * ErrorBoundary di sezione — replica React.lazy fallback ma per crash di
 * rendering sincroni. Audit 2026-05-28 (Davide bug "Impostazioni
 * white-screen"): aprendo una sezione con un crash JS non catturato, in
 * assenza di boundary l'intera UI Electron resta bianca. Wrappare ogni
 * sezione isola il crash al box guasto, lascia il resto della pagina
 * navigabile e mostra a Davide un messaggio + bottone "Riprova" senza
 * bisogno di riavviare l'app.
 *
 * NOTA: React tratta come error boundary SOLO i class component che
 * implementano ``getDerivedStateFromError`` o ``componentDidCatch``. Non
 * esiste equivalente hook (2026), quindi qui restiamo intenzionalmente
 * su una classe sebbene il resto del codebase sia function-component.
 */
interface ErrorBoundaryProps {
  /** Label opzionale mostrata nel fallback (es. "Sezione Sincronizzazioni"). */
  label?: string;
  /** Nodo custom da renderizzare al posto del fallback di default. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div
        role="alert"
        className="rounded-md border border-red/20 bg-red-soft px-4 py-3 text-[12.5px] text-ink"
      >
        <div className="mb-1 font-medium">
          {this.props.label ? `Errore nella sezione "${this.props.label}"` : "Errore di rendering"}
        </div>
        <div className="mb-2 font-mono text-[11.5px] text-ink-2">
          {error.message || "Errore sconosciuto"}
        </div>
        <button
          type="button"
          onClick={this.reset}
          className="rounded-md border border-red/30 bg-card px-3 py-1 text-[11.5px] text-red hover:border-red/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Riprova
        </button>
      </div>
    );
  }
}
